import { useCallback, useEffect, useState } from 'react';

import { PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';
import { Select, StandardSpinner } from '@dreryk/react-components';

import { guardianTranslationKeys } from '../../../../../translations';
import { GuardianExternalIdentifierType } from '../../../common/formSections/personalData/guardianExternalIdentifier/GuardianExternalIdentifierTypeField';
import { GuardianExternalIdentifierValue } from '../../../common/formSections/personalData/guardianExternalIdentifier/GuardianExternalIdentifierValueField';
import { GuardianMedicalLeaveFormController, GuardianMedicalLeaveFormGuardian } from '../../guardianMedicalLeave.types';
import {
   createGuardianExternalIdentifier,
   GuardianModal,
   GuardianModalFormSubmitHandler,
} from '../guardianData/GuardianModal/GuardianModal';

import {
   useGuardianSelectItems,
   useMedicalLeaveGuardiansQuery,
   useSetAddress,
   useSetBirthDate,
} from './GuardianSelect.hooks';
import { GuardianSelectDefaultGuardian } from './GuardianSelectDefaultGuardian';
import { StyledGuardianSelect } from './StyledGuardianSelect';
import { StyledGuardianSelectLimiter } from './StyledGuardianSelectLimiter';
import { StyledGuardianSelectPesel } from './StyledGuardianSelectPesel';

type GuardianSelectProps = {
   readonly controller: GuardianMedicalLeaveFormController;
   readonly patientId: number;
   readonly initialGuardian?: GuardianMedicalLeaveFormGuardian;
   readonly patientExternalIdentifier: PatientExternalIdentifier;
   readonly shouldUseDefaultGuardianAddress: boolean;
};

const triggerlabelExtractor = (guardian?: GuardianMedicalLeaveFormGuardian): string => {
   if (!guardian) {
      return '';
   }

   const externalIdentifierValue = guardian.guardianExternalIdentifier.accept<string>({
      visitPesel: (pesel) => pesel.asString(),
      visitPassport: (passport) => passport.getValue(),
      visitNationalIdentificationNumber: (nationalIdentificationNumber) => nationalIdentificationNumber.getValue(),
   });

   return `${guardian.firstName} ${guardian.lastName}, ${externalIdentifierValue}`;
};

type GuardianSelectItemProps = {
   readonly guardian: GuardianMedicalLeaveFormGuardian;
};

const GuardianSelectItem = (props: GuardianSelectItemProps): JSX.Element => (
   <span>
      {props.guardian.firstName} {props.guardian.lastName}
      <StyledGuardianSelectPesel>
         {props.guardian.guardianExternalIdentifier.accept<string>({
            visitPesel: (pesel) => pesel.asString(),
            visitPassport: (passport) => passport.getValue(),
            visitNationalIdentificationNumber: (nationalIdentificationNumber) =>
               nationalIdentificationNumber.getValue(),
         })}
      </StyledGuardianSelectPesel>
   </span>
);

// eslint-disable-next-line max-lines-per-function
export const GuardianSelect = (props: GuardianSelectProps): JSX.Element => {
   const { initialGuardian, patientExternalIdentifier } = props;
   const [manuallyAddedGuardians, setManuallyAddedGuardians] = useState<
      GuardianMedicalLeaveFormGuardian[] | undefined
   >();
   const { data: guardians, loading, fetched } = useMedicalLeaveGuardiansQuery(props.patientId);
   const items = useGuardianSelectItems(guardians ?? [], initialGuardian, manuallyAddedGuardians);
   const setAddress = useSetAddress(props.controller);
   const setBirthDate = useSetBirthDate(props.controller);

   useEffect(() => {
      if (guardians && initialGuardian && fetched) {
         props.controller.setValue('guardian', initialGuardian);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [guardians, fetched, initialGuardian]);

   const handleSelect = useCallback(
      (selectedGuardian: GuardianMedicalLeaveFormGuardian | null) => {
         if (selectedGuardian?.relationCode) {
            props.controller.setValue('relationCode', selectedGuardian.relationCode);
         }
         if (selectedGuardian) {
            setAddress(selectedGuardian.address ?? undefined);
            setBirthDate(selectedGuardian.birthDate ?? undefined);
         }
         props.controller.setValue('payers', []);
      },
      [props.controller, setAddress, setBirthDate]
   );

   const checkGuardianAlreadyAdded = useCallback(
      (type: GuardianExternalIdentifierType, value: GuardianExternalIdentifierValue): boolean => {
         return items.some((guardian) =>
            guardian.guardianExternalIdentifier.accept<boolean>({
               visitPesel: (pesel) => type === 'Pesel' && pesel.asString() === value,
               visitPassport: (passport) => type === 'Passport' && passport.getValue() === value,
               visitNationalIdentificationNumber: () => assertUnreachable(undefined),
            })
         );
      },
      [items]
   );

   const checkGuardianSameAsPatient = useCallback(
      (type: GuardianExternalIdentifierType, value: GuardianExternalIdentifierValue): boolean => {
         return patientExternalIdentifier.accept<boolean>({
            visitPesel: (pesel) => type === 'Pesel' && pesel.asString() === value,
            visitGuardianPesel: () => false,
            visitPassport: (passport) => type === 'Passport' && passport.getValue() === value,
            visitNationalIdentificationNumber: () => false,
         });
      },
      [patientExternalIdentifier]
   );

   const onGuardianModalSubmit = useCallback<GuardianModalFormSubmitHandler>(
      (formData) => {
         const guardianToUpdate: GuardianMedicalLeaveFormGuardian = {
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            guardianExternalIdentifier: createGuardianExternalIdentifier(formData),
            birthDate: formData.birthDate,
            address: formData.address ?? null,
         };
         setManuallyAddedGuardians((currentAddedGuardians) => [...(currentAddedGuardians ?? []), guardianToUpdate]);
         props.controller.setValue('guardian', guardianToUpdate);
         setAddress(formData.address ?? undefined);
         setBirthDate(formData.birthDate ?? undefined);
      },
      [props.controller, setAddress, setBirthDate]
   );

   if (loading) {
      return <StandardSpinner />;
   }

   return (
      <>
         <StyledGuardianSelect>
            {items.length > 0 && (
               <StyledGuardianSelectLimiter>
                  <Select<GuardianMedicalLeaveFormGuardian>
                     triggerLabelExtractor={triggerlabelExtractor}
                     controller={props.controller.register('guardian', { required: true })}
                     placeholder={guardianTranslationKeys.chooseGuardian}
                     testId={'guardian'}
                     onSelect={handleSelect}
                  >
                     <Select.List items={items} keyExtractor={'id'}>
                        {(guardian) => (
                           <Select.Item
                              testId={guardian.id}
                              item={guardian}
                              label={<GuardianSelectItem guardian={guardian} />}
                           />
                        )}
                     </Select.List>
                  </Select>
               </StyledGuardianSelectLimiter>
            )}
            <GuardianModal
               onSubmit={onGuardianModalSubmit}
               checkGuardianAlreadyAdded={checkGuardianAlreadyAdded}
               checkGuardianSameAsPatient={checkGuardianSameAsPatient}
               patientId={props.patientId}
            />
         </StyledGuardianSelect>
         {guardians && fetched && items.length === 1 ? (
            <GuardianSelectDefaultGuardian
               controller={props.controller}
               guardian={items[0]}
               shouldUseDefaultGuardianAddress={props.shouldUseDefaultGuardianAddress}
            />
         ) : null}
      </>
   );
};
